// Fachlogik der Begehungs-Durchführung, der Vier-Augen-Prüfung und der
// Bewertung (ERP/CRM-Plan Abschnitt 7 „Workflow" Schritte 3–6 — Phase D4).
//
// Hier steht ausschliesslich, was sich OHNE Datenbank entscheiden laesst: wie
// die Antwort der RPC in die Typen des Bestands uebersetzt wird, welche Zeilen
// ueberhaupt zurueckgeschrieben werden, ob ein Bericht einreichbar ist, wie ein
// Rating aggregiert wird und in welcher Reihenfolge die Pruefliste steht. Der
// unreine Teil (RPC-Aufrufe, Upload) liegt in freelancerPortalService.ts bzw.
// assignmentService.ts.
//
// Kein jsdom im Projekt (Standing Decision) — deshalb pur und mit Vitest
// geprueft.
//
// Tabellen, RLS und RPCs: supabase/freelancer_inspection.sql
// (MIGRATIONS.md Nr. 21).

import { ChecklistEntry, ChecklistCategory, ChecklistStatus, InspectionItem } from '../types';
import { isVirtualId } from './checklistLogic';
import { AssignmentStatus, InspectionAssignment } from './assignmentLogic';

// ─── Was die Begehung braucht ───────────────────────────────────────────────

/**
 * Bauabschnitt (Los) eines Projekts. Aus `data.metadata.sections`, aber
 * bewusst NUR id und Name: die vollstaendige Abschnittszeile traegt
 * Begehungszaehler und Nummernkreise, und die sind internes Rechenwerk.
 */
export interface InspectionSection {
    id: string;
    name: string;
}

/**
 * Eine am Bau beteiligte Firma — die Auswahl fuer „Verantwortliche Firma" am
 * Mangel. OHNE E-Mail und Telefon: der Freelancer ordnet einen Mangel zu, er
 * verschickt nichts (der Verteiler ist Grundlage des INTERNEN Versands).
 */
export interface InspectionCompany {
    company: string;
    role?: string;
    tradeType?: string;
}

export interface InspectionProjektKopf {
    id: string;
    name: string;
    projectNumber?: string;
    address?: string;
    coordinatorName?: string;
}

/** Das vollstaendige Ergebnis von `freelancer_load_inspection`. */
export interface FreelancerInspectionData {
    assignmentId: string;
    status: AssignmentStatus;
    zeitraumVon: string;
    zeitraumBis: string;
    verguetungCents: number;
    reportId?: string;
    projekt: InspectionProjektKopf;
    sections: InspectionSection[];
    checklists: ChecklistEntry[];
    items: InspectionItem[];
    companies: InspectionCompany[];
    /** Begruendung der letzten Nachbesserung. '' wenn es keine gab. */
    pruefkommentar: string;
}

const text = (v: unknown): string => (v == null ? '' : String(v));
const optional = (v: unknown): string | undefined => {
    const s = text(v).trim();
    return s === '' ? undefined : s;
};

/**
 * Antwort der RPC in die Bestandstypen uebersetzen.
 *
 * Die RPC liefert snake_case (sie baut das jsonb aus den Spalten), der Client
 * rechnet in camelCase — dieselbe Grenze wie in `cloudService.loadProjectById`,
 * nur eine Ebene hoeher. Fehlt ein Block, ist er leer und nicht `undefined`:
 * die Oberflaeche soll nicht zwischen „keine" und „nicht geladen" unterscheiden
 * muessen.
 */
export const mapInspectionResponse = (raw: any): FreelancerInspectionData | null => {
    if (!raw || typeof raw !== 'object') return null;
    const a = raw.assignment || {};
    const p = raw.project || {};
    if (!a.id) return null;

    return {
        assignmentId: String(a.id),
        status: (a.status || 'angenommen') as AssignmentStatus,
        zeitraumVon: text(a.zeitraum_von),
        zeitraumBis: text(a.zeitraum_bis),
        verguetungCents: Number(a.verguetung_cents ?? 0),
        reportId: optional(a.report_id),
        projekt: {
            id: text(p.id),
            name: text(p.name) || 'Unbenanntes Projekt',
            projectNumber: optional(p.project_number),
            address: optional(p.project_address),
            coordinatorName: optional(p.coordinator_name),
        },
        sections: (Array.isArray(raw.sections) ? raw.sections : [])
            .filter((s: any) => s && text(s.id) !== '')
            .map((s: any) => ({ id: text(s.id), name: text(s.name) || 'Abschnitt' })),
        checklists: (Array.isArray(raw.checklists) ? raw.checklists : []).map(
            (c: any): ChecklistEntry => ({
                id: text(c.id),
                text: text(c.text),
                category: (c.category || 'Allgemein') as ChecklistCategory,
                isRelevant: c.is_relevant !== false,
                lastChecked: c.last_checked ? text(c.last_checked).slice(0, 10) : null,
                status: (c.status ?? null) as ChecklistStatus,
                comment: text(c.comment),
                isCustom: !!c.is_custom,
                sectionId: optional(c.section_id),
            }),
        ),
        items: (Array.isArray(raw.items) ? raw.items : []).map(
            (i: any): InspectionItem => ({
                id: text(i.id),
                ticketNumber: i.ticket_number != null ? Number(i.ticket_number) : undefined,
                title: text(i.title),
                location: text(i.location),
                observation: text(i.description),
                regulation: text(i.regulation),
                responsiblePerson: text(i.responsible_person),
                deadline: i.deadline ? text(i.deadline).slice(0, 10) : '',
                immediateAction: !!i.immediate_action,
                dateFound: i.date_found ? text(i.date_found).slice(0, 10) : undefined,
                status: (i.status || 'open') as InspectionItem['status'],
                photos: Array.isArray(i.photos) ? i.photos.map(text).filter(Boolean) : [],
                sectionId: optional(i.section_id),
                closedDate: i.closed_at ? text(i.closed_at).slice(0, 10) : undefined,
                statusComment: optional(i.status_comment),
                closedImage: optional(i.closed_image),
            }),
        ),
        companies: (Array.isArray(raw.companies) ? raw.companies : [])
            .filter((k: any) => k && text(k.company).trim() !== '')
            .map((k: any) => ({
                company: text(k.company).trim(),
                role: optional(k.role),
                tradeType: optional(k.trade_type),
            }))
            .sort((x: InspectionCompany, y: InspectionCompany) =>
                x.company.localeCompare(y.company, 'de')),
        pruefkommentar: text(raw.pruefkommentar).trim(),
    };
};

// ─── Zurueckschreiben ───────────────────────────────────────────────────────

export interface InspectionPayload {
    checklists: Record<string, unknown>[];
    items: Record<string, unknown>[];
}

/**
 * Payload fuer `freelancer_save_inspection`.
 *
 * Spaltennamen wortgleich zum internen Speicherpfad
 * (cloudService.saveProjectToCloud, childSyncs) — die RPC schreibt in DIESELBEN
 * Tabellen, und zwei Schreibwege mit unterschiedlicher Feldbenennung waeren
 * zwei Wahrheiten.
 *
 * VIRTUELLE Checklisteneintraege (`virtual-*`, checklistLogic) fliegen raus:
 * sie sind Anzeigezeilen der Vorlage, die noch nie bearbeitet wurden. Sie
 * mitzuschicken erzeugte fuer jeden Punkt der Master-Liste eine Datenzeile,
 * auch fuer die 20, die niemand angefasst hat.
 *
 * `project_id` steht bewusst NICHT im Payload: die RPC setzt sie aus dem
 * Auftrag. Ein Client, der sie mitschicken duerfte, koennte sie faelschen.
 */
export const buildInspectionPayload = (
    checklists: ChecklistEntry[] | null | undefined,
    items: InspectionItem[] | null | undefined,
): InspectionPayload => ({
    checklists: (checklists || [])
        .filter((c) => c && c.id && !isVirtualId(c.id))
        .map((c) => ({
            id: c.id,
            text: c.text || '',
            category: c.category,
            status: c.status ?? null,
            comment: c.comment || '',
            last_checked: c.lastChecked || null,
            is_relevant: c.isRelevant !== false,
            is_custom: !!c.isCustom,
            section_id: c.sectionId || null,
        })),
    items: (items || [])
        .filter((i) => i && i.id)
        .map((i) => ({
            id: i.id,
            ticket_number: i.ticketNumber ?? null,
            title: i.title || '',
            description: i.observation || '',
            location: i.location || '',
            responsible_person: i.responsiblePerson || '',
            deadline: i.deadline || null,
            immediate_action: !!i.immediateAction,
            date_found: i.dateFound || null,
            status: i.status || 'open',
            photos: (i.photos || []).filter((f) => !!f && f !== 'placeholder'),
            regulation: i.regulation || '',
            section_id: i.sectionId || null,
            closed_at: i.closedDate || null,
            status_comment: i.statusComment || '',
            closed_image: i.closedImage || null,
        })),
});

/**
 * Ordner fuer Begehungsfotos im Eimer `project-assets`.
 *
 * WORTGLEICH zum Pfadschema, das die Storage-Policy
 * `project_assets_freelancer_insert` erlaubt (freelancer_inspection.sql,
 * Abschnitt 8): `<project_id>/freelancer/<assignment_id>`. `uploadAsset`
 * haengt daran `/<hash>.<ext>` — der fertige Pfad hat damit genau die drei
 * Abschnitte, die `freelancer_may_write_asset` prueft.
 *
 * Wer diesen String aendert, macht jeden Foto-Upload des Portals zu einem
 * RLS-Fehler.
 */
export const inspectionFotoPfad = (projectId: string, assignmentId: string): string =>
    `${projectId}/freelancer/${assignmentId}`;

// ─── Bearbeitbarkeit und Einreichen ─────────────────────────────────────────

/**
 * Zustaende, in denen der Freelancer an der Begehung arbeitet. Spiegel des
 * Guards `freelancer_assignment_project` (freelancer_inspection.sql, 4) — wer
 * diese Liste aendert, muss sie dort aendern.
 */
export const BEGEHUNG_BEARBEITBAR: AssignmentStatus[] = [
    'angenommen',
    'durchgefuehrt',
    'nachbesserung',
];

export const kannBegehungBearbeiten = (
    a: Pick<InspectionAssignment, 'status'> | null | undefined,
): boolean => !!a && BEGEHUNG_BEARBEITBAR.includes(a.status);

/**
 * Der Zielstatus des Hauptknopfes einer Begehung.
 *
 *   angenommen     → durchgefuehrt        („Begehung abschliessen")
 *   durchgefuehrt  → bericht_eingereicht  („Bericht einreichen")
 *   nachbesserung  → bericht_eingereicht  („Nachbesserung einreichen")
 *
 * null, wenn es keinen naechsten Schritt gibt. Spiegel der Matrix aus
 * `set_assignment_status`; die Datenbank entscheidet verbindlich noch einmal.
 */
export const naechsterBegehungsSchritt = (
    status: AssignmentStatus | null | undefined,
): AssignmentStatus | null => {
    if (status === 'angenommen') return 'durchgefuehrt';
    if (status === 'durchgefuehrt' || status === 'nachbesserung') return 'bericht_eingereicht';
    return null;
};

export const BEGEHUNGS_SCHRITT_LABELS: Partial<Record<AssignmentStatus, string>> = {
    durchgefuehrt: 'Begehung abschließen',
    bericht_eingereicht: 'Bericht einreichen',
};

/**
 * Was fehlt noch, bevor der Bericht eingereicht werden darf?
 *
 * Dieselbe Regel wie der interne Versand-Gate in SiGeKoInspectionModule
 * (`handleFinishAndSend`): ein OFFENER Mangel ohne Titel, Beschreibung, Ort
 * oder verantwortliche Firma ist im Bericht eine leere Zeile, und im
 * Zweifelsfall die, um die es geht. Erledigte und informative Eintraege sind
 * bewusst ausgenommen — sie tragen die Aussage schon im Status.
 *
 * Rueckgabe ist eine Liste deutscher Klartexte; leer = einreichbar.
 */
export const einreichHindernisse = (
    items: InspectionItem[] | null | undefined,
): string[] => {
    const offeneUnvollstaendig = (items || []).filter(
        (i) =>
            i &&
            i.status === 'open' &&
            (!i.title?.trim() ||
                !i.observation?.trim() ||
                !i.location?.trim() ||
                !i.responsiblePerson?.trim()),
    );
    if (offeneUnvollstaendig.length === 0) return [];
    return [
        offeneUnvollstaendig.length === 1
            ? '1 offener Mangel ist unvollständig — Titel, Feststellung, Ort und verantwortliche Firma sind Pflicht.'
            : `${offeneUnvollstaendig.length} offene Mängel sind unvollständig — Titel, Feststellung, Ort und verantwortliche Firma sind Pflicht.`,
    ];
};

/** Kurzstatistik fuer den Kopf der Begehung. */
export interface BegehungsZahlen {
    geprueft: number;
    offen: number;
    maengelOffen: number;
    maengelErledigt: number;
    fotos: number;
}

export const begehungsZahlen = (
    checklists: ChecklistEntry[] | null | undefined,
    items: InspectionItem[] | null | undefined,
): BegehungsZahlen => {
    let geprueft = 0;
    let offen = 0;
    for (const c of checklists || []) {
        if (!c || !c.isRelevant) continue;
        if (c.status === 'ok' || c.status === 'nok') geprueft += 1;
        else offen += 1;
    }
    let maengelOffen = 0;
    let maengelErledigt = 0;
    let fotos = 0;
    for (const i of items || []) {
        if (!i) continue;
        if (i.status === 'open') maengelOffen += 1;
        else if (i.status === 'closed') maengelErledigt += 1;
        fotos += (i.photos || []).filter(Boolean).length;
    }
    return { geprueft, offen, maengelOffen, maengelErledigt, fotos };
};

// ─── Vier-Augen-Prüfung (Innendienst) ───────────────────────────────────────

/** Der Status, in dem ein Auftrag auf die Pruefung wartet. */
export const PRUEFUNG_STATUS: AssignmentStatus = 'bericht_eingereicht';

export const istZurPruefung = (
    a: Pick<InspectionAssignment, 'status'> | null | undefined,
): boolean => !!a && a.status === PRUEFUNG_STATUS;

/** Zahl der offenen Pruefungen — das Abzeichen an der Freelancer-Ansicht. */
export const offenePruefungenZahl = (
    assignments: InspectionAssignment[] | null | undefined,
): number => (assignments || []).filter(istZurPruefung).length;

/**
 * Reihenfolge der Prueflisten: das laengst Wartende zuerst. Anders als in der
 * Auftragsliste (Beginn ABSTEIGEND, der juengste oben) ist die Prueflisten-
 * Frage „was liegt am laengsten?" — und darauf antwortet nur die aufsteigende
 * Sortierung nach Ende des Leistungszeitraums.
 */
export const sortiereReviewQueue = (
    assignments: InspectionAssignment[] | null | undefined,
): InspectionAssignment[] =>
    [...(assignments || [])].sort((a, b) => {
        const v = (a.zeitraumBis || '').localeCompare(b.zeitraumBis || '');
        if (v !== 0) return v;
        return (a.createdAt || '').localeCompare(b.createdAt || '');
    });

/**
 * Darf der Kommentar so abgeschickt werden? Spiegel der Pflichtpruefung in
 * `set_assignment_status` (Nachbesserung ohne Begruendung ist keine).
 */
export const NACHBESSERUNG_MIN_LAENGE = 5;

export const kommentarFehler = (
    ziel: AssignmentStatus,
    kommentar: string | null | undefined,
): string | null => {
    if (ziel !== 'nachbesserung') return null;
    const t = (kommentar || '').trim();
    if (t.length < NACHBESSERUNG_MIN_LAENGE) {
        return 'Bitte kurz begründen, was nachzuarbeiten ist — der Freelancer sieht genau diesen Text.';
    }
    return null;
};

// ─── Bewertung ──────────────────────────────────────────────────────────────

export interface AssignmentRating {
    id: string;
    assignmentId: string;
    freelancerId: string;
    qualitaet: number;
    zuverlaessigkeit: number;
    kommentar?: string;
    ratedBy: string;
    createdAt: string;
}

/**
 * Note EINER Bewertung: Mittel aus Qualitaet und Zuverlaessigkeit, auf zwei
 * Nachkommastellen. Identische Rechnung wie der Trigger
 * `refresh_freelancer_rating` (freelancer_inspection.sql, 2a) — die Oberflaeche
 * soll denselben Wert anzeigen, den die Datenbank speichert, und nicht einen,
 * der um eine Rundung danebenliegt.
 */
export const ratingNote = (qualitaet: number, zuverlaessigkeit: number): number =>
    Math.round(((qualitaet + zuverlaessigkeit) / 2) * 100) / 100;

/**
 * Aggregat ueber mehrere Bewertungen — der Wert, der in `freelancers.rating`
 * landet. `null` bei leerer Liste: 0 waere ausserhalb des CHECK (1–5) und
 * hiesse ausserdem „sehr schlecht" statt „unbekannt".
 */
export const aggregiereRatings = (
    ratings: Pick<AssignmentRating, 'qualitaet' | 'zuverlaessigkeit'>[] | null | undefined,
): number | null => {
    const gueltig = (ratings || []).filter(
        (r) => r && Number.isFinite(r.qualitaet) && Number.isFinite(r.zuverlaessigkeit),
    );
    if (gueltig.length === 0) return null;
    const summe = gueltig.reduce((s, r) => s + (r.qualitaet + r.zuverlaessigkeit) / 2, 0);
    return Math.round((summe / gueltig.length) * 100) / 100;
};

/** Ist die Bewertung vollstaendig? Beide Dimensionen, beide 1–5. */
export const ratingVollstaendig = (
    qualitaet: number | null | undefined,
    zuverlaessigkeit: number | null | undefined,
): boolean =>
    !!qualitaet && !!zuverlaessigkeit &&
    qualitaet >= 1 && qualitaet <= 5 &&
    zuverlaessigkeit >= 1 && zuverlaessigkeit <= 5;

/** Bewertbar ist eine geprueft oder bereits abgerechnete Leistung. */
export const kannBewerten = (
    a: Pick<InspectionAssignment, 'status'> | null | undefined,
): boolean => !!a && (a.status === 'geprueft' || a.status === 'abgerechnet');
